import { bloomQuestions, bloomThemes, isScenarioQuestion } from "./questions";
import { scoreToTier } from "./results-garden";
import type {
  BloomAnswers,
  BloomQuestionWithTheme,
  BloomThemeScore,
} from "../types/bloom-content";

const SCALE_MAX = 4;
const POINTS_PER_QUESTION = SCALE_MAX;
const QUESTIONS_PER_THEME = 6;

const TIER_LABELS: Record<number, string> = {
  1: "Tijd om hier aandacht aan te geven",
  2: "Aan het groeien, maar extra verzorging nodig",
  3: "In volle bloei",
};

export function scoreAnswer(
  value: number,
  reversed = false,
  kind?: BloomQuestionWithTheme["kind"]
): number {
  if (value < 0 || value > SCALE_MAX) {
    throw new Error(`Answer value must be 0–${SCALE_MAX}, got ${value}`);
  }
  if (kind === "scenario") return value;
  return reversed ? SCALE_MAX - value : value;
}

export function scoreThemeQuestions(
  questions: BloomQuestionWithTheme[],
  answers: BloomAnswers
): number {
  return questions.reduce((sum, q) => {
    const value = answers[q.id];
    if (value === undefined) return sum;
    return sum + scoreAnswer(value, q.reversed, q.kind);
  }, 0);
}

export function themeScoreLabel(score: number, maxScore: number): string {
  return TIER_LABELS[scoreToTier(score, maxScore)];
}

export function computeThemeScores(answers: BloomAnswers): BloomThemeScore[] {
  return bloomThemes.map((theme) => {
    const themeQuestions = bloomQuestions.filter((q) => q.themeId === theme.id);
    const score = scoreThemeQuestions(themeQuestions, answers);
    return {
      themeId: theme.id,
      label: theme.label,
      summary: theme.summary,
      score,
      maxScore: themeQuestions.length * POINTS_PER_QUESTION,
    };
  });
}

/** ponytail: smallest check that fails if reversed scoring or max score breaks */
export function runScoringSelfCheck(): void {
  if (scoreAnswer(0, true) !== 4) throw new Error("reversed(0) should be 4");
  if (scoreAnswer(4, true) !== 0) throw new Error("reversed(4) should be 0");
  if (scoreAnswer(2, false) !== 2) throw new Error("normal(2) should be 2");
  if (scoreAnswer(2, true, "scenario") !== 2) throw new Error("scenario(2) should be 2");
  if (themeScoreLabel(0, 24) !== TIER_LABELS[1]) throw new Error("label(0)");
  if (themeScoreLabel(24, 24) !== TIER_LABELS[3]) throw new Error("label(24)");
  if (themeScoreLabel(7, 24) !== TIER_LABELS[1]) throw new Error("label(7) should be tier 1");
  if (themeScoreLabel(8, 24) !== TIER_LABELS[2]) throw new Error("label(8) should be tier 2");
  if (themeScoreLabel(15, 24) !== TIER_LABELS[2]) throw new Error("label(15) should be tier 2");
  if (themeScoreLabel(16, 24) !== TIER_LABELS[3]) throw new Error("label(16) should be tier 3");

  const maxThemeScore = QUESTIONS_PER_THEME * POINTS_PER_QUESTION;
  if (maxThemeScore !== 24) throw new Error("max theme score should be 24");

  const allMax: BloomAnswers = {};
  for (const q of bloomQuestions) {
    allMax[q.id] = SCALE_MAX;
  }
  const scores = computeThemeScores(allMax);
  for (const s of scores) {
    const themeQs = bloomQuestions.filter((t) => t.themeId === s.themeId);
    const expectedMax = themeQs.reduce(
      (sum, q) => sum + scoreAnswer(SCALE_MAX, q.reversed, q.kind),
      0
    );
    if (s.score !== expectedMax) {
      throw new Error(`Theme ${s.themeId} max score mismatch: ${s.score} vs ${expectedMax}`);
    }
  }

  const scenarioCount = bloomQuestions.filter((q) => isScenarioQuestion(q)).length;
  if (scenarioCount !== 12) throw new Error("expected 12 scenario questions");
}

runScoringSelfCheck();
